import { getFieldComponent } from "../config/fieldComponents";
import { getFieldMinDate } from "../config/fieldMinDates";
import { TextField } from "../blocks/fields/TextField";
import { LongTextField } from "../blocks/fields/LongTextField";
import { EmailField } from "../blocks/fields/EmailField";
import { NumberField } from "../blocks/fields/NumberField";
import { ColourField } from "../blocks/fields/ColourField";
import { SpacingField } from "../blocks/fields/SpacingField";
import { ImageField } from "../blocks/fields/ImageField";
import { RichTextEditor } from "../blocks/richtext/RichTextEditor";
import { MoneyField } from "../blocks/fields/MoneyField";
import { BooleanField } from "../blocks/fields/BooleanField";
import { CheckboxField } from "../blocks/fields/CheckboxField";
import { DateField } from "../blocks/fields/DateField";
import { DateTimeField } from "../blocks/fields/DateTimeField";
import { TimeField } from "../blocks/fields/TimeField";
import { RadioField } from "../blocks/fields/RadioField";
import { PicklistField } from "../blocks/fields/PicklistField";
import { LookupField } from "../blocks/fields/LookupField";
import { EntityLookupField } from "../blocks/fields/EntityLookupField";
import { MultiSelectField } from "../blocks/fields/MultiSelectField";
import { FileUploadField } from "../blocks/fields/FileUploadField";
import { StatusBadgeField } from "../blocks/fields/StatusBadgeField";
import { ComponentField } from "../blocks/fields/ComponentField";
import { lazy, Suspense } from "react";
import { ReadOnlyField } from "../blocks/fields/ReadOnlyField";
import { ReadOnlyEntityLookupField } from "../blocks/fields/ReadOnlyEntityLookupField";
import { FieldLinkConfig } from "./fieldConfig.types";
import { fieldDisplayText } from "./fieldDisplayText";
import { FieldRendererProps } from "./FieldRenderer.types";

// Already displays rather than inputs, so they render the same on a read-only page.
const NON_INPUT_TYPES = new Set(["statusbadge", "component", "image", "richtext"]);

// Lazy so a page without links doesn't bring the router in behind every field.
const RecordLinkField = lazy(() =>
  import("../blocks/fields/RecordLinkField").then((module) => ({
    default: module.RecordLinkField,
  }))
);

function linkRoute(link: FieldLinkConfig, values: Record<string, unknown> | undefined) {
  if (link.to) {
    return link.to;
  }

  const entityName = link.entityKey ? values?.[link.entityKey] : undefined;
  return typeof entityName === "string"
    ? link.routes?.[entityName.toLowerCase()]
    : undefined;
}

/**
 * Dispatches a declarative field config to the matching block component, which is what
 * makes a stage data rather than hand-written JSX.
 */
export function FieldRenderer({
  field,
  value,
  onChange,
  values,
  disabled,
  error,
  preview,
  asText,
}: FieldRendererProps) {
  // A designer preview has no record to show, so it keeps the field's own control.
  const showAsText =
    asText &&
    !preview &&
    !NON_INPUT_TYPES.has(field.type) &&
    !(field.type === "radio" && field.cards);

  if (showAsText && field.type === "lookup" && field.lookupEntity && !field.link) {
    return (
      <ReadOnlyEntityLookupField
        label={field.label}
        helpText={field.helpText}
        name={field.name}
        lookupEntity={field.lookupEntity}
        value={value}
      />
    );
  }

  if (showAsText && !field.link) {
    return (
      <ReadOnlyField
        label={field.label}
        helpText={field.helpText}
        name={field.name}
        text={fieldDisplayText(field, value)}
      />
    );
  }

  if (field.link && disabled && !preview) {
    return (
      <Suspense fallback={null}>
        <RecordLinkField
          label={field.label}
          helpText={field.helpText}
          name={field.name}
          value={typeof value === "string" ? value : null}
          to={linkRoute(field.link, values)}
          id={values?.[field.link.idKey]}
          linkText={field.link.text}
        />
      </Suspense>
    );
  }

  switch (field.type) {
    case "text":
      return (
        <TextField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    case "longtext":
      return (
        <LongTextField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          rows={field.rows}
          placeholder={field.placeholder}
        />
      );
    case "email":
      return (
        <EmailField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    case "richtext":
      return (
        <RichTextEditor
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          mergeEntities={field.mergeEntities}
        />
      );
    case "image":
      return (
        <ImageField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case "spacing":
      return (
        <SpacingField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
        />
      );
    case "colour":
      return (
        <ColourField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder}
        />
      );
    case "number":
      return (
        <NumberField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as number | null}
          onChange={onChange as (value: number | null) => void}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder}
          stepper={field.stepper}
        />
      );
    case "money":
      return (
        <MoneyField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as number | null}
          onChange={onChange as (value: number | null) => void}
          disabled={disabled}
          required={field.required}
          currency={field.currency}
          placeholder={field.placeholder}
        />
      );
    case "boolean":
      return (
        <BooleanField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as boolean}
          onChange={onChange as (value: boolean) => void}
          disabled={disabled}
          layout={field.layout}
        />
      );
    case "checkbox":
      return (
        <CheckboxField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as boolean}
          onChange={onChange as (value: boolean) => void}
          disabled={disabled}
        />
      );
    case "date":
      return (
        <DateField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          max={field.max}
          min={field.min}
        />
      );
    case "time":
      return (
        <TimeField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
        />
      );
    case "datetime":
      return (
        <DateTimeField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          storedAsLocal={field.storedAsLocal}
          min={getFieldMinDate(field.minDateKey)?.(values ?? {})}
          preview={preview}
        />
      );
    case "radio":
      return (
        <RadioField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string | number | null}
          options={field.options}
          onChange={
            onChange as (value: string | number | null) => void
          }
          disabled={disabled}
          inline={field.inline}
          cards={field.cards}
          icons={field.icons}
        />
      );
    case "picklist":
      return (
        <PicklistField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          value={value as string | number | null}
          options={field.options}
          onChange={onChange}
          disabled={disabled}
          required={field.required}
          placeholder={field.placeholder}
          // A required choice can't go back to empty, so once made the empty option is
          // clutter. A new record keeps it, or the first option would look chosen.
          hidePlaceholder={
            Boolean(field.required) &&
            value !== null &&
            value !== undefined &&
            value !== ""
          }
        />
      );
    case "multiselect":
      return (
        <MultiSelectField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          // The service sends and accepts the selected option values.
          value={Array.isArray(value) ? value : []}
          options={field.options}
          onChange={onChange as (value: (string | number)[]) => void}
          disabled={disabled}
          placeholder={field.placeholder}
        />
      );
    case "lookup": {
      const lookupProps = {
        label: field.label,
        helpText: field.helpText,
        error,
        name: field.name,
        // A warehouse id can arrive as a number; the select holds strings.
        value: typeof value === "number" ? String(value) : (value as string | null),
        onChange,
        disabled,
        required: field.required,
        placeholder: field.placeholder,
        searchable: field.searchable,
      };

      return field.lookupEntity ? (
        <EntityLookupField
          {...lookupProps}
          lookupEntity={field.lookupEntity}
          optionFilterKey={field.optionFilterKey}
          searchFilter={field.searchFilter}
          manageRoute={field.manageRoute}
          record={values}
        />
      ) : (
        <LookupField {...lookupProps} options={field.options ?? []} />
      );
    }
    case "fileupload":
      return (
        <FileUploadField
          label={field.label}
          helpText={field.helpText}
          error={error}
          name={field.name}
          currentFileName={value as string | null}
          onChange={onChange as (file: File | null) => void}
          disabled={disabled}
          accept={field.accept}
        />
      );
    case "statusbadge":
      return (
        <StatusBadgeField
          label={field.label}
          helpText={field.helpText}
          value={value as string}
          variant={field.variant}
        />
      );
    case "component": {
      const component = getFieldComponent(field.componentKey);
      return component ? (
        <ComponentField
          label={field.label}
          name={field.name}
          helpText={field.helpText}
          error={field.showsOwnErrors ? undefined : error}
          component={component}
          componentProps={field.componentProps}
        />
      ) : null;
    }
    default:
      return null;
  }
}
